import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, IconButton, Skeleton, useTheme, alpha, darken, lighten,
} from '@mui/material';
import { MoreVert, DragIndicator } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../services/admissions/admissionApplicationService';
import { AdmissionCycle, AdmissionApplication, ApplicationStatus } from '../../types/admissions';
import StatusChip, { statusLabel } from '../common/StatusChip';
import CandidateDrawer from './CandidateDrawer';
import ApplicationActionsMenu from './ApplicationActionsMenu';
import TransitionDialog, { nextStatuses } from './dialogs/TransitionDialog';
import { fmtClassName } from '../../utils/pdf/format';
import { thinScrollbar } from '../../utils/scrollbar';
import { TabToolbar } from './tabLayout';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import PersonAvatar from '../common/PersonAvatar';
import { photoFilenameOf } from '../../utils/personPhoto';

/**
 * PipelineKanban — the cycle's applications as a left-to-right funnel.
 *
 * Drag a card onto another stage to move it. While dragging, the stages the
 * application can actually move to light up and the rest fade, using the
 * same transition rules as the Change status dialog. A drop opens that
 * dialog with the target stage pre-selected, because a move can raise an
 * invoice or change seat counts — staff confirm it and can record a reason.
 * Click a card to open the applicant; ⋮ has the usual actions.
 */

interface Props {
  cycle: AdmissionCycle;
}

interface Column {
  key: string;
  label: string;
  hint: string;
  statuses: ApplicationStatus[];
  /** Lane accent — tints the lane, its dot and title. */
  color: string;
}

const CLOSED_STATUSES: ApplicationStatus[] = ['Ineligible', 'Rejected', 'NotAdmitted', 'OfferDeclined', 'OfferLapsed', 'Withdrawn'];

const CLOSED_COLOR = '#8a94a6';

const COLUMNS: Column[] = [
  { key: 'intake',     label: 'Intake',     hint: 'New and awaiting the application fee', color: '#e07a3f', statuses: ['Draft', 'Submitted', 'AppFeePending'] },
  { key: 'review',     label: 'Review',     hint: 'Checking the form and documents',      color: '#d4a017', statuses: ['UnderReview', 'DocsRequired'] },
  { key: 'assessment', label: 'Assessment', hint: 'Test or interview',                    color: '#6c5ce7', statuses: ['AssessmentScheduled', 'Absent', 'Assessed'] },
  { key: 'decision',   label: 'Decision',   hint: 'Offered or on the waitlist',           color: '#2f80ed', statuses: ['Offered', 'Waitlisted'] },
  { key: 'confirming', label: 'Confirming', hint: 'Accepted, finalising fee',             color: '#0fa3a3', statuses: ['OfferAccepted', 'ConfirmedConditional', 'AdmFeePending', 'Confirmed'] },
  { key: 'enrolled',   label: 'Enrolled',   hint: 'Handed over as students',              color: '#2e9e5b', statuses: ['Enrolled'] },
  // Each card's chip says why it closed; an ineligible one can be dragged back to Intake or Review.
  { key: 'closed',     label: 'Closed',     hint: 'Ineligible, rejected, withdrawn or lapsed', color: CLOSED_COLOR, statuses: CLOSED_STATUSES },
];

const CARD_LIMIT = 1000; // soft safety cap on fetch

const PipelineKanban: React.FC<Props> = ({ cycle }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [apps, setApps] = useState<AdmissionApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState(false);

  const [openId, setOpenId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; app: AdmissionApplication } | null>(null);
  const [dragging, setDragging] = useState<AdmissionApplication | null>(null);
  const [overColumn, setOverColumn] = useState<string | null>(null);
  const [move, setMove] = useState<{ app: AdmissionApplication; targets: ApplicationStatus[] } | null>(null);
  // Dragging changes status: needs `admissions` write, and decision moves
  // also need `admissions_decisions` (same rules as the backend).
  const { canWrite } = useModulePermissions();
  const canManage = canWrite('admissions');
  const canDecide = canWrite('admissions_decisions');
  const canMove = useCallback(
    (app: AdmissionApplication) => canManage && nextStatuses(app, cycle, canDecide).length > 0,
    [canManage, canDecide, cycle],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // One fetch grouped on the client — a cycle holds hundreds of
      // applications, not millions.
      const res = await admissionApplicationService.list({ cycleId: cycle._id, limit: CARD_LIMIT, page: 1 });
      setApps(res.items);
      setTruncated(res.pagination.totalItems > res.items.length);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load pipeline', { variant: 'error' });
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, [cycle._id, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const map = new Map<string, AdmissionApplication[]>(COLUMNS.map(c => [c.key, []] as [string, AdmissionApplication[]]));
    for (const a of apps) {
      const col = COLUMNS.find(c => c.statuses.includes(a.status));
      if (col) map.get(col.key)!.push(a);
    }
    return map;
  }, [apps]);

  /** Stages in `column` the dragged application may move to. */
  const dropTargets = useCallback((column: Column): ApplicationStatus[] => {
    if (!dragging) return [];
    const allowed = nextStatuses(dragging, cycle, canDecide);
    return column.statuses.filter(s => allowed.includes(s));
  }, [dragging, cycle, canDecide]);

  const endDrag = () => { setDragging(null); setOverColumn(null); };

  const onDrop = (column: Column) => {
    if (!dragging) return;
    const targets = dropTargets(column);
    const app = dragging;
    endDrag();
    if (targets.length > 0) setMove({ app, targets });
    else if (!column.statuses.includes(app.status)) {
      enqueueSnackbar(`${app.name} can't move from ${statusLabel({ kind: 'application', value: app.status })} to ${column.label}`, { variant: 'info' });
    }
  };

  const closedCount = grouped.get('closed')?.length ?? 0;
  const activeTotal = apps.length - closedCount;

  return (
    <Box>
      {/* ── Toolbar ───────────────────────────────────────────────── */}
      <TabToolbar
        onRefresh={load}
        refreshing={loading}
        summary={loading ? 'Loading…' : (
          <>
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{activeTotal} in progress</Box>
            {closedCount > 0 && ` · ${closedCount} closed`}
            {truncated && ` · showing the first ${CARD_LIMIT}`}
            {' · drag a card to move it to another stage'}
          </>
        )}
      />
      <Box sx={{ height: 20 }} />

      {/* ── Board ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', px: 2.5, pb: 2.5, alignItems: 'flex-start', ...thinScrollbar(theme.palette.text.primary) }}>
        {COLUMNS.map(col => {
          const targets = dropTargets(col);
          const state: LaneState = !dragging
            ? 'idle'
            : col.statuses.includes(dragging.status) ? 'source'
            : targets.length > 0 ? (overColumn === col.key ? 'over' : 'target')
            : 'blocked';
          return (
            <Lane
              key={col.key}
              column={col}
              apps={grouped.get(col.key) || []}
              loading={loading}
              state={state}
              draggingId={dragging?._id}
              onDragOver={() => { if (targets.length) setOverColumn(col.key); }}
              onDragLeave={() => setOverColumn(c => (c === col.key ? null : c))}
              onDrop={() => onDrop(col)}
              onCardClick={setOpenId}
              onCardMenu={(e, app) => setMenu({ anchor: e.currentTarget, app })}
              onCardDragStart={setDragging}
              onCardDragEnd={endDrag}
              canMove={canMove}
            />
          );
        })}
      </Box>

      <ApplicationActionsMenu
        app={menu?.app ?? null}
        cycle={cycle}
        anchorEl={menu?.anchor ?? null}
        onClose={() => setMenu(null)}
        onMutated={load}
      />
      {move && (
        <TransitionDialog
          open
          application={move.app}
          cycle={cycle}
          targets={move.targets}
          onClose={() => setMove(null)}
          onDone={async () => { setMove(null); await load(); }}
        />
      )}
      {openId && (
        <CandidateDrawer applicationId={openId} cycle={cycle} onClose={() => setOpenId(null)} onMutated={load} />
      )}
    </Box>
  );
};

// ─── Lane ────────────────────────────────────────────────────────────────────

type LaneState = 'idle' | 'source' | 'target' | 'over' | 'blocked';

interface LaneProps {
  column: Column;
  apps: AdmissionApplication[];
  loading: boolean;
  state: LaneState;
  draggingId?: string;
  onDragOver?: () => void;
  onDragLeave?: () => void;
  onDrop?: () => void;
  onCardClick: (id: string) => void;
  onCardMenu: (e: React.MouseEvent<HTMLElement>, app: AdmissionApplication) => void;
  onCardDragStart?: (app: AdmissionApplication) => void;
  onCardDragEnd?: () => void;
  canMove?: (app: AdmissionApplication) => boolean;
}

const Lane: React.FC<LaneProps> = ({
  column, apps, loading, state, draggingId,
  onDragOver, onDragLeave, onDrop, onCardClick, onCardMenu, onCardDragStart, onCardDragEnd, canMove,
}) => {
  const theme = useTheme();
  const tone = column.color;
  const dark = theme.palette.mode === 'dark';
  const droppable = state === 'target' || state === 'over';

  return (
    <Box
      onDragOver={(e) => { if (droppable) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onDragOver?.(); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) onDragLeave?.(); }}
      onDrop={(e) => { e.preventDefault(); onDrop?.(); }}
      sx={{
        width: 272,
        flexShrink: 0,
        borderRadius: 3,
        p: 1,
        // Light: a wash of the stage colour. Dark: a quiet panel edged in it,
        // since colour washes turn muddy on a dark page.
        ...(dark
          ? {
              bgcolor: state === 'over' ? alpha(tone, 0.12) : alpha(theme.palette.text.primary, 0.025),
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `inset 0 2px 0 ${alpha(tone, 0.85)}`,
            }
          : { bgcolor: alpha(tone, state === 'over' ? 0.16 : 0.07) }),
        outline: droppable ? `2px dashed ${alpha(tone, state === 'over' ? 0.9 : 0.5)}` : '2px dashed transparent',
        outlineOffset: -2,
        opacity: state === 'blocked' ? 0.45 : 1,
        transition: 'background-color 120ms ease, opacity 120ms ease, outline-color 120ms ease',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 1, pt: 0.5, pb: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone, flexShrink: 0 }} />
          <Typography variant="body2" fontWeight={700} sx={{ color: dark ? lighten(tone, 0.3) : darken(tone, 0.15) }}>{column.label}</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ color: dark ? 'text.secondary' : alpha(tone, 0.9), flex: 1 }}>{apps.length}</Typography>
        </Stack>
        {column.hint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, pl: 2 }}>
            {droppable ? 'Drop here to move' : column.hint}
          </Typography>
        )}
      </Box>

      {/* Cards */}
      <Stack spacing={1} sx={{ maxHeight: 'calc(100vh - 340px)', minHeight: 120, overflowY: 'auto', px: 0.25, pb: 0.25, ...thinScrollbar(tone) }}>
        {loading ? (
          [1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={92} sx={{ borderRadius: 2 }} />)
        ) : apps.length === 0 ? (
          <Box sx={{
            py: 4, textAlign: 'center', borderRadius: 2,
            border: `1px dashed ${alpha(tone, 0.35)}`, color: 'text.secondary',
          }}>
            <Typography variant="caption">No applications</Typography>
          </Box>
        ) : (
          apps.map(app => (
            <Card
              key={app._id}
              app={app}
              dimmed={draggingId === app._id}
              draggable={!!onCardDragStart && !!canMove?.(app)}
              onClick={() => onCardClick(app._id)}
              onMenu={(e) => onCardMenu(e, app)}
              onDragStart={() => onCardDragStart?.(app)}
              onDragEnd={() => onCardDragEnd?.()}
            />
          ))
        )}
      </Stack>
    </Box>
  );
};

// ─── Card ────────────────────────────────────────────────────────────────────

interface CardProps {
  app: AdmissionApplication;
  dimmed: boolean;
  draggable: boolean;
  onClick: () => void;
  onMenu: (e: React.MouseEvent<HTMLElement>) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

const Card: React.FC<CardProps> = ({ app, dimmed, draggable, onClick, onMenu, onDragStart, onDragEnd }) => {
  const theme = useTheme();
  const className = typeof app.classId === 'object' ? fmtClassName(app.classId?.name) : '';

  return (
    <Box
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', app._id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(15,23,42,0.04)',
        cursor: draggable ? 'grab' : 'pointer',
        opacity: dimmed ? 0.4 : 1,
        transition: 'box-shadow 120ms ease, border-color 120ms ease, transform 120ms ease, opacity 120ms ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
          boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 4px 12px rgba(15,23,42,0.08)',
          '& .grip': { opacity: 1 },
        },
        '&:active': { cursor: draggable ? 'grabbing' : 'pointer' },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <PersonAvatar name={app.name} photoFilename={photoFilenameOf(app)} size={32} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{app.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
            {app.applicationNumber}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={`Actions for ${app.name}`}
          onClick={(e) => { e.stopPropagation(); onMenu(e); }}
          sx={{ mt: -0.5, mr: -0.75, color: 'text.secondary' }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.25 }}>
        <StatusChip status={{ kind: 'application', value: app.status }} />
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[className, app.computedMeritScore != null && `Merit ${app.computedMeritScore.toFixed(1)}`].filter(Boolean).join(' · ')}
          </Typography>
          {draggable && (
            <DragIndicator className="grip" sx={{ fontSize: 16, color: 'text.disabled', opacity: 0, transition: 'opacity 120ms' }} />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PipelineKanban;
